import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../contexts/ToastContext';
import { neo4jService } from '../../services/neo4jService';
import { Package, Truck, CheckCircle, AlertCircle, ArrowLeft, Store } from 'lucide-react';
import './Orders.css';

const getOrderImage = (order) => order.productImage || order.main_image || order.image || '';

const OrderStatus = ({ status }) => {
  const getStatusIcon = () => {
    switch (status.toLowerCase()) {
      case 'processing':
        return <Package className="status-icon processing" />;
      case 'pickup requested':
      case 'ready for pickup':
        return <Store className="status-icon pickup" />;
      case 'picked up':
        return <CheckCircle className="status-icon delivered" />;
      case 'shipped':
        return <Truck className="status-icon shipped" />;
      case 'delivered':
        return <CheckCircle className="status-icon delivered" />;
      default:
        return <AlertCircle className="status-icon" />;
    }
  };

  return (
    <div className={`order-status ${status.toLowerCase()}`}>
      {getStatusIcon()}
      <span>{status}</span>
    </div>
  );
};

function formatPickupSchedule(order) {
  if (order.pickupScheduledAt) {
    return new Date(order.pickupScheduledAt).toLocaleString([], {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  }

  if (order.pickupDate && order.pickupTime) {
    return new Date(`${order.pickupDate}T${order.pickupTime}`).toLocaleString([], {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  }

  return '';
}

const Orders = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    async function fetchOrders() {
      try {
        const orderData = await neo4jService.getCustomerOrders(currentUser.id || currentUser.email);
        setOrders(orderData);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
        toast.error('Failed to load orders');
      } finally {
        setLoading(false);
      }
    }

    fetchOrders();
  }, [currentUser, navigate]);

  if (loading) {
    return <div className="loading">Loading your orders...</div>;
  }

  if (orders.length === 0) {
    return (
      <div className="orders-container empty">
        <div className="page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          <h1>My Orders</h1>
        </div>
        <div className="empty-orders">
          <Package size={48} />
          <h2>No Orders Yet</h2>
          <p>When you make a purchase, your orders will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-container">
      <div className="page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <h1>My Orders</h1>
      </div>
      <div className="orders-list">
        {orders.map((order) => {
          const orderImage = getOrderImage(order);

          return (
          <div key={order.orderId} className="order-card">
            <div className="order-header">
              <div className="order-info">
                <span className="order-date">
                  {new Date(order.orderDate).toLocaleDateString()}
                </span>
                <span className="order-id">Order #{order.orderId}</span>
              </div>
              <OrderStatus status={order.status} />
            </div>
            <div className="order-content">
              <div className="product-info">
                {orderImage ? (
                  <img src={orderImage} alt={order.productName} />
                ) : (
                  <div className="order-image-placeholder">
                    <Package size={28} />
                  </div>
                )}
              <div className="product-details">
                  <h3>{order.productName}</h3>
                  <p className="quantity">Quantity: {order.quantity}</p>
                  <p className="price">${(order.price * order.quantity).toFixed(2)}</p>
                  {order.fulfillmentType === 'pickup' && (
                    <p className="pickup-method">In-store pickup</p>
                  )}
                </div>
              </div>
              {order.fulfillmentType === 'pickup' && (
                <div className="tracking-info pickup-info">
                  {formatPickupSchedule(order) && (
                    <p>Pickup time: {formatPickupSchedule(order)}</p>
                  )}
                  <p>Pickup contact: {order.pickupName || currentUser.name || 'Customer'}</p>
                  {order.pickupPhone && <p>Phone: {order.pickupPhone}</p>}
                  {order.pickupNotes && <p>Notes: {order.pickupNotes}</p>}
                </div>
              )}
              {order.trackingNumber && (
                <div className="tracking-info">
                  <p>Tracking Number: {order.trackingNumber}</p>
                  <button 
                    className="track-button"
                    onClick={() => window.open(`/track/${order.trackingNumber}`, '_blank')}
                  >
                    Track Order
                  </button>
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default Orders;
